import styled from '@emotion/styled';

import type { Prismic } from '@landsbankinn/prismic';
import { PrismicLink, WrapText } from '@landsbankinn/prismic';
import {
  Column,
  device,
  Icon,
  theme,
  typography,
  useMedia,
} from '@landsbankinn/ui';
import React from 'react';

enum PointsType {
  EMPTY = 'Empty',
  NUMBERS = 'Numbers',
  TEXT = 'Text',
  ICON = 'Icon',
}

enum PointColor {
  AUTO = 'Auto',
  BLUE = 'Blue',
  LIGHTBLUE = 'LightBlue',
  CYAN = 'Cyan',
  ORANGE = 'Orange',
}

interface IProps {
  icon?: string;
  label?: string;
  copy: React.ReactNode;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  numberText?: React.ReactNode;
  isSplitterLong?: boolean;
  cols?: number;
  pointColor?: string;
  number?: string | number;
  pointsType?: string;
  isBottomAligned?: boolean;
  action?: React.ReactNode;
  isDisabled?: boolean;
  isBodySize?: boolean;
  isNumberTextLarge?: boolean;
  isNumberRightAligned?: boolean;
  pointLink?: Prismic.Link;
}

interface ICopy {
  cols: number;
  isBottomAligned?: boolean;
  isDisabled?: boolean;
  isBodySize?: boolean;
}

const autoColor = (props: any) => {
  const colors = [
    props.theme.colors.primary,
    props.theme.shades.secondary4,
    props.theme.shades.primary3,
    props.theme.shades.ternary4,
    props.theme.shades.secondary3,
    props.theme.shades.primary2,
  ];
  const styles = {};
  for (let index = 0; index < colors.length; index++) {
    (styles as any)[`&:nth-of-type(${index + 1})`] = {
      color: colors[index],
    };
  }
  return styles;
};

const Col = styled(Column)<{ pointColor?: string }>`
  display: flex;
  flex-flow: column nowrap;
  color: ${(props) => {
    switch (props.pointColor) {
      case PointColor.AUTO:
        return autoColor;
      case PointColor.BLUE:
        return props.theme.shades.primary3;
      case PointColor.LIGHTBLUE:
        return props.theme.shades.primary2;
      case PointColor.CYAN:
        return props.theme.shades.ternary5;
      case PointColor.ORANGE:
        return props.theme.colors.secondary;
    }
    return props.theme.colors.foreground;
  }};
`;

const Host = styled.div<{ isDisabled?: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  flex: 1;
  width: 100%;

  ${(props) => props.isDisabled && 'opacity: 0.1;'}

  @media ${device.tablet} {
    margin-bottom: 4em;
  }
`;

const IconWrap = styled.div`
  min-height: 55px;
  width: 48px;
  margin-left: 21px;
  g,
  path {
    stroke: currentColor;
  }
`;

const Number = styled.div`
  ${typography.numbersMedium}
  color: currentColor;
`;

const MobileTopContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  margin-bottom: 25px;

  @media ${device.tablet} {
    flex-direction: row;
    align-items: center;
    margin-bottom: 0;

    & > * + * {
      margin-left: 34px;
    }
  }
`;

const Label = styled.div`
  font-weight: bold;
  color: ${(props) => props.theme.colors.foreground};
`;

const Divider = styled.div<{ isLong?: boolean; noMarginTop: boolean }>`
  width: ${(props) => (props.isLong ? '100%' : '77px')};
  height: 4px;
  background-color: currentColor;
  margin: ${(props) => (props.noMarginTop ? '0 0 19px' : '19px 0')};
`;

const Copy = styled.div<ICopy>`
  color: ${(props) => props.theme.colors.foreground};
  max-width: 500px;

  padding-right: 70px;
  margin-bottom: 44px;

  &:last-child {
    margin-bottom: 0;
  }

  p {
    margin: 16px 0;
    ${(props) => props.isDisabled && 'padding: 0 !important;'};
    ${(props) =>
      props.isDisabled && `background-color: ${theme.shades.primary2}`};
  }

  p:first-child {
    margin: 16px 0;
    padding: 0;
    ${(props) => props.isDisabled && 'max-width: 70%;'};
  }

  div {
    p {
      margin: 16px 0;
      padding: 0;
    }

    a {
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  @media ${device.tablet} {
    padding-right: 0;
  }

  @media ${device.desktop} {
    ${(props) => {
      if (!props.isBottomAligned) return 'padding-right: 15px';

      switch (props.cols) {
        case 6:
          return `
            padding-right: 140px;
            ${!props.isBodySize && 'font-size: 24px;'}
            ${typography.p2(props)}
          `;
        case 4:
          return `
            padding-right: 70px;
            ${!props.isBodySize && 'font-size: 24px;'}
            ${typography.p2(props)}
          `;
        case 3:
          return `
            padding-right: 50px;
          `;
        case 2.4:
          return `
            padding-right: 30px;
          `;
        case 2:
          return `
            padding-right: 0px;
            font-size: 14px;
            ${typography.bodySmall(props)}
        `;
      }

      return '';
    }}
  }
`;

const NumbersWrap = styled.div<{ isRightAligned?: boolean }>`
  display: flex;
  flex-flow: row wrap;
  align-items: baseline;

  @media ${device.tablet} {
    justify-content: ${(props) =>
      props.isRightAligned ? 'flex-end' : 'flex-start'};
  }
`;

const Prefix = styled.div`
  ${typography.bodyEditorial}
  padding-right: 7px;
`;

const LargeNumber = styled.div<{ cols: number; isLarge?: boolean }>`
  ${(props) =>
    props.isLarge
      ? typography.numbersBig(props)
      : typography.numbersBiggest(props)}
  font-weight: ${(props) => props.isLarge && '500'};

  @media ${device.desktop} {
    ${(props) => {
      switch (props.cols) {
        case 6:
        case 4:
          return `
            ${typography.numbersBig(props)}
          `;
        case 3:
          return `
            font-size: 40px;
          `;
        case 2.4:
          return `
          font-size: 30px;
          `;
        case 2:
          return `
            font-size: 30px;
        `;
      }

      return '';
    }}
  }
`;

const Suffix = styled.div`
  ${typography.bodyEditorial}
  padding-left: 7px;
`;

const Action = styled.div`
  margin-top: 44px;
  margin-top: auto;
`;

const Top = (props: IProps) => {
  const { icon, label, number, pointsType } = props;

  switch (pointsType) {
    case PointsType.ICON:
      return (
        <IconWrap>
          <Icon name={icon ?? ''} />
        </IconWrap>
      );
    case PointsType.NUMBERS:
      return <Number>{number}</Number>;
    case PointsType.TEXT:
      return <Label>{label}</Label>;
  }
  return null;
};

export const PointCard = (props: IProps) => {
  const {
    copy,
    numberText,
    prefix,
    suffix,
    isSplitterLong,
    cols = 12,
    pointColor,
    pointsType,
    isBottomAligned,
    action = null,
    isDisabled,
    isBodySize,
    isNumberTextLarge,
    isNumberRightAligned,
    pointLink,
    ...rest
  } = props;
  const isMobile = useMedia('sm');
  return (
    <Col
      md={{
        width:
          cols > 5
            ? 6 / 12
            : cols < 3
            ? 4 / 12
            : cols === 3
            ? 6 / 12
            : cols / 12,
      }}
      lg={{ width: cols / 12 }}
      pointColor={pointColor}
      {...rest}
    >
      <>
        {pointLink && (
          <PrismicLink value={pointLink}>
            <a>
              <Host isDisabled={isDisabled} aria-hidden={!!isDisabled}>
                <Top {...props} />
                <Divider
                  isLong={isSplitterLong}
                  noMarginTop={pointsType === PointsType.EMPTY}
                />
                {copy && (
                  <Copy
                    cols={cols}
                    isBottomAligned={isBottomAligned}
                    isDisabled={isDisabled}
                    isBodySize={isBodySize}
                  >
                    {copy}
                  </Copy>
                )}
                {numberText && (
                  <NumbersWrap isRightAligned={isNumberRightAligned}>
                    {prefix && <Prefix>{prefix}</Prefix>}
                    <LargeNumber cols={cols} isLarge={isNumberTextLarge}>
                      {numberText}
                    </LargeNumber>
                    {suffix && <Suffix>{suffix}</Suffix>}
                  </NumbersWrap>
                )}
                {action && <Action>{action}</Action>}
              </Host>
            </a>
          </PrismicLink>
        )}
        {!pointLink && (
          <Host isDisabled={isDisabled} aria-hidden={!!isDisabled}>
            {isMobile && (
              <>
                <Divider
                  isLong={isSplitterLong}
                  noMarginTop={pointsType === PointsType.EMPTY}
                />
                <MobileTopContainer>
                  <Top {...props} />
                  {copy && (
                    <Copy
                      cols={cols}
                      isBottomAligned={isBottomAligned}
                      isDisabled={isDisabled}
                      isBodySize={isBodySize}
                    >
                      {copy}
                    </Copy>
                  )}
                </MobileTopContainer>
              </>
            )}

            {!isMobile && (
              <>
                <Top {...props} />
                <Divider
                  isLong={isSplitterLong}
                  noMarginTop={pointsType === PointsType.EMPTY}
                />
                {copy && (
                  <Copy
                    cols={cols}
                    isBottomAligned={isBottomAligned}
                    isDisabled={isDisabled}
                    isBodySize={isBodySize}
                  >
                    {copy}
                  </Copy>
                )}
              </>
            )}

            {numberText && (
              <NumbersWrap isRightAligned={isNumberRightAligned}>
                {prefix && <Prefix>{prefix}</Prefix>}
                <LargeNumber cols={cols} isLarge={isNumberTextLarge}>
                  {numberText}
                </LargeNumber>
                {suffix && <Suffix>{suffix}</Suffix>}
              </NumbersWrap>
            )}
            {action && <Action>{action}</Action>}
          </Host>
        )}
      </>
    </Col>
  );
};
